import React, { createContext, useContext, useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';
import { requestForToken, onMessageListener } from '../lib/firebase';
import apiClient from '../api/api';

const SocketContext = createContext();

export const useSocket = () => useContext(SocketContext);

export const SocketProvider = ({ children }) => {
    const { user, isAuthenticated, refetchUser } = useAuth();
    const [socket, setSocket] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [unreadMessageCount, setUnreadMessageCount] = useState(0);

    // Fetch initial unread message count
    useEffect(() => {
        if (isAuthenticated) {
            apiClient.get('/messages').then(res => {
                const totalUnread = res.data.reduce((acc, conv) => acc + (conv.unreadCount || 0), 0);
                setUnreadMessageCount(totalUnread);
            }).catch(console.error);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (isAuthenticated && (user?.userId || user?.profileId)) {
            // --- 1. Socket.io Connection ---
            const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
            const newSocket = io(backendUrl, {
                withCredentials: true,
                transports: ['websocket', 'polling']
            });

            newSocket.on('connect', () => {
                console.log('Connected to Socket.io server');
                newSocket.emit('register', user.profileId || user.userId);
            });

            // --- 2. Push Notification Setup ---
            const setupPush = async () => {
                const fcmToken = await requestForToken();
                if (fcmToken) {
                    try {
                        await apiClient.post('/users/fcm-token', { fcmToken });
                        console.log("FCM Token saved to server");
                    } catch (err) {
                        console.error("Failed to save FCM token", err);
                    }
                }
            };
            setupPush();

            // Foreground listener
            onMessageListener()
                .then((payload) => {
                    console.log("New foreground push:", payload);
                    toast.success(payload.notification.title + ": " + payload.notification.body);
                })
                .catch((err) => console.log("failed: ", err));

            // --- 3. Listeners ---
            newSocket.on('new_notification', (notif) => {
                setNotifications(prev => [notif, ...prev]);
                
                // Show Toast
                toast((t) => (
                    <div className="flex items-center justify-between min-w-[300px]">
                        <div className="flex-1 pr-4">
                            <p className="text-xs font-black text-emerald-600 uppercase tracking-widest mb-1">New Alert</p>
                            <p className="text-sm font-medium text-slate-700">{notif.message}</p>
                        </div>
                        {notif.link && (
                            <button 
                                onClick={() => {
                                    window.location.href = notif.link;
                                    toast.dismiss(t.id);
                                }}
                                className="bg-emerald-600 text-white px-3 py-1.5 rounded-lg text-xs font-bold"
                            >
                                Open
                            </button>
                        )}
                    </div>
                ), { duration: 5000, position: 'top-right' });

                if ("Notification" in window && Notification.permission === "granted") {
                    new Notification("Service24", { body: notif.message });
                }
            });

            newSocket.on('new_message', (msg) => {
                // If NOT on messages page, increment count
                if (window.location.pathname !== '/messages') {
                    setUnreadMessageCount(prev => prev + 1);
                    
                    toast((t) => (
                        <div className="flex items-center gap-4 min-w-[280px]">
                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
                                {msg.sender?.name?.charAt(0) || 'U'}
                            </div>
                            <div className="flex-1">
                                <p className="text-sm font-bold text-slate-800">{msg.sender?.name || 'New Message'}</p>
                                <p className="text-xs text-slate-500 line-clamp-1">{msg.content}</p>
                            </div>
                            <button 
                                onClick={() => {
                                    window.location.href = '/messages';
                                    toast.dismiss(t.id);
                                }}
                                className="text-blue-600 text-xs font-black uppercase tracking-widest"
                            >
                                Reply
                            </button>
                        </div>
                    ), { duration: 4000 });
                }
            });

            newSocket.on('verification_status_change', async (data) => {
                console.log("Verification status changed via socket:", data);
                await refetchUser();
                // Optional: Show a toast/alert
                alert(`Your account verification status has been updated to: ${data.is_Identity_Verified ? 'VERIFIED' : 'PENDING'}`);
            });

            setSocket(newSocket);

            return () => {
                newSocket.disconnect();
                console.log('Socket disconnected');
            };
        } else {
            if (socket) {
                socket.disconnect();
                setSocket(null);
            }
        }
    }, [isAuthenticated, user?.userId, user?.profileId]);

    const value = {
        socket,
        notifications,
        unreadMessageCount,
        setUnreadMessageCount,
        clearNotifications: () => setNotifications([])
    };

    return (
        <SocketContext.Provider value={value}>
            {children}
        </SocketContext.Provider>
    );
};
